import React, { useState, useMemo } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Person as PersonIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { useBranding } from '../../utils/BrandingContext';
import { isHelpdeskMode } from '../../utils/helpdeskMode';
import loginBg1 from '../../assets/login-bg-1.webp';
import loginBg2 from '../../assets/login-bg-2.webp';
import loginBg3 from '../../assets/login-bg-3.webp';
import loginBg4 from '../../assets/login-bg-4.webp';
import loginBg5 from '../../assets/login-bg-5.webp';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from;
  const { login, verifyOtp, isLoading, error, clearError, twoFactorRequired, clearTwoFactor } = useAuthStore();
  const { login: portalLogin, isLoading: portalLoading, error: portalError, clearError: portalClearError } = usePortalAuthStore();
  const { branding } = useBranding();

  // Tab mode — only relevant on helpdesk.* domains
  const [loginMode, setLoginMode] = useState<'staff' | 'customer'>('staff');

  // Customer portal form state
  const [portalEmail, setPortalEmail] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const [portalShowPw, setPortalShowPw] = useState(false);

  const handlePortalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    portalClearError();
    try {
      await portalLogin(window.location.hostname, portalEmail, portalPassword);
      navigate('/portal/tickets', { replace: true });
    } catch { /* error set in store */ }
  };

  // Prefer server-provided image URL; fall back to bundled defaults for legacy values.
  const selectedBg = useMemo(() => {
    if (branding.loginBackgroundImage) return branding.loginBackgroundImage;

    const loginBackgrounds: Record<string, string> = {
      '1': loginBg1,
      '2': loginBg2,
      '3': loginBg3,
      '4': loginBg4,
      '5': loginBg5,
    };

    const themeKey = branding.loginBackgroundTheme || '1';
    return loginBackgrounds[themeKey] || loginBg1;
  }, [branding.loginBackgroundImage, branding.loginBackgroundTheme]);

  const brandedDomain = useMemo(() => {
    const host = window.location.hostname;
    if (!host || host === 'localhost' || host === '127.0.0.1') return '';
    if (host === 'cruvzmail.com' || host.endsWith('.cruvzmail.com')) return '';
    const parts = host.split('.');
    const mailSubdomains = ['mail', 'webmail', 'smtp', 'imap', 'pop', 'mx', 'm', 'email'];
    if (parts.length > 2 && mailSubdomains.includes(parts[0].toLowerCase())) {
      return parts.slice(1).join('.');
    }
    return host;
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [otp, setOtp] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const emailDomain = email.includes('@') && email.split('@')[1] ? email.split('@')[1] : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();
    const normalizedEmail = email.trim().toLowerCase();
    const identifier = (brandedDomain && !normalizedEmail.includes('@'))
      ? `${normalizedEmail}@${brandedDomain.toLowerCase()}`
      : normalizedEmail;
    if (!identifier || !password) {
      setLocalError('Please enter both your email and password');
      return;
    }
    try {
      const requires2FA = await login(identifier, password);
      if (!requires2FA) navigate(from ? `${from.pathname}${from.search}` : '/mail/inbox');
    } catch { /* handled by store */ }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();
    if (!otp || otp.length !== 6) {
      setLocalError('Please enter a valid 6-digit code');
      return;
    }
    try {
      await verifyOtp(twoFactorRequired?.email || email, otp);
      navigate(from ? `${from.pathname}${from.search}` : '/mail/inbox');
    } catch { /* handled by store */ }
  };

  const handleResendOtp = async () => {
    setLocalError('');
    clearError();
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const identifier = (brandedDomain && !normalizedEmail.includes('@'))
        ? `${normalizedEmail}@${brandedDomain.toLowerCase()}`
        : normalizedEmail;
      await login(identifier, password);
      alert('OTP resent successfully!');
    } catch { /* handled by store */ }
  };

  const handleBackToLogin = () => {
    clearTwoFactor();
    setOtp('');
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      bgcolor: '#ffffff',
      '& fieldset': { borderColor: '#d1d5db', borderWidth: 1 },
      '&:hover fieldset': { borderColor: '#9ca3af' },
      '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 2 },
    },
    '& .MuiInputBase-input': { fontSize: 14, color: '#111827', paddingTop: '12px', paddingBottom: '12px' },
  };

  return (
    /* ── Full-screen photo background ── */
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
        backgroundImage: `url(${selectedBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#07111f',
      }}
    >
      {/* ── Full-screen dark overlay: darker left, lighter right ── */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          background: 'linear-gradient(90deg, rgba(4,14,32,0.82) 0%, rgba(4,14,32,0.58) 50%, rgba(4,14,32,0.28) 100%)',
        }}
      />

      {/* ── LEFT — Branding text ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '55%',
          position: 'relative',
          zIndex: 1,
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: '36px 48px 32px',
        }}
      >
        {/* ── TOP: Logo + Stamp badge ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <img
              src={branding.sidebarIcon || branding.logo || '/logo-icon.png'}
              alt={branding.companyName}
              onError={(e) => { const t = e.currentTarget; t.onerror = null; t.src = '/logo-icon.png'; }}
              style={{
                maxWidth: 220, height: 64,
                objectFit: 'contain',
                borderRadius: '10px',
                imageRendering: '-webkit-optimize-contrast',
                flexShrink: 0,
              }}
            />
            {branding.companyName && (
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 19, letterSpacing: '-0.2px' }}>
                {branding.companyName}
                <Box component="sup" sx={{ fontSize: 10, fontWeight: 400, verticalAlign: 'super', ml: 0.2 }}>®</Box>
              </Typography>
            )}
          </Box>

          {/* "DELIVERED WORLDWIDE" circular stamp */}
          <Box
            sx={{
              width: 78, height: 78,
              borderRadius: '50%',
              border: '2px dashed rgba(212,175,80,0.6)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 0.3,
              position: 'relative',
            }}
          >
            {/* Envelope icon inside stamp */}
            <Box sx={{ fontSize: 17, lineHeight: 1, mb: 0.2 }}>✉️</Box>
            <Typography sx={{
              fontSize: 6, letterSpacing: '1.6px', fontWeight: 700,
              color: 'rgba(212,175,80,0.85)', textTransform: 'uppercase', lineHeight: 1.5,
            }}>
              Delivered
            </Typography>
            <Typography sx={{
              fontSize: 5.5, letterSpacing: '1.2px',
              color: 'rgba(212,175,80,0.65)', textTransform: 'uppercase', lineHeight: 1.2,
            }}>
              Worldwide
            </Typography>
          </Box>
        </Box>

        {/* ── CENTER: Headline + Features ── */}
        <Box>
          <Typography
            sx={{
              fontFamily: '"Inter", "Segoe UI", sans-serif',
              fontSize: { md: '2.1rem', lg: '2.6rem' },
              fontWeight: 800,
              color: '#ffffff',
              lineHeight: 1.15,
              letterSpacing: '-0.5px',
              mb: 0.3,
            }}
          >
            Secure Business Email,
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", "Segoe UI", sans-serif',
              fontSize: { md: '2.1rem', lg: '2.6rem' },
              fontWeight: 800,
              color: '#d4a832',
              lineHeight: 1.15,
              letterSpacing: '-0.5px',
              mb: 1.8,
            }}
          >
            Simplified.
          </Typography>

          <Typography sx={{
            fontFamily: '"Inter", "Segoe UI", sans-serif',
            color: 'rgba(210,228,255,0.75)',
            fontSize: 14.5,
            lineHeight: 1.65,
            maxWidth: 340,
            mb: 3.5,
          }}>
            Fast, reliable and secure email hosting designed for your business.
          </Typography>

          {/* Features */}
          {[
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6l-8-4z" fill="rgba(212,175,80,0.85)" />
                </svg>
              ),
              title: 'Advanced Security',
              desc: 'Enterprise-grade protection against spam, phishing and threats.',
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="4" width="20" height="16" rx="2" fill="rgba(212,175,80,0.85)" />
                  <path d="M2 8l10 7 10-7" stroke="#07111f" strokeWidth="1.5" />
                </svg>
              ),
              title: '99.99% Uptime',
              desc: 'Reliable email service you can count on, every single time.',
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2a10 10 0 0 1 7.39 16.74L21 21l-2.26-1.61A10 10 0 1 1 12 2z" fill="rgba(212,175,80,0.85)" />
                </svg>
              ),
              title: 'Priority Support',
              desc: '24/7 expert support whenever you need us.',
            },
          ].map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.8, mb: i < 2 ? 2.2 : 0 }}>
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                  border: '1.5px solid rgba(212,175,80,0.55)',
                  bgcolor: 'rgba(212,175,80,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {f.icon}
              </Box>
              <Box>
                <Typography sx={{
                  fontFamily: '"Inter", "Segoe UI", sans-serif',
                  color: '#d4a832',
                  fontWeight: 700,
                  fontSize: 13.5,
                  mb: 0.25,
                }}>
                  {f.title}
                </Typography>
                <Typography sx={{
                  fontFamily: '"Inter", "Segoe UI", sans-serif',
                  color: 'rgba(196,215,255,0.65)',
                  fontSize: 12.5,
                  lineHeight: 1.5,
                }}>
                  {f.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Copyright */}
        <Typography sx={{ color: 'rgba(180,200,240,0.35)', fontSize: 11.5 }}>
          © {new Date().getFullYear()}{branding.companyName ? ` ${branding.companyName}.` : ''} All rights reserved.
        </Typography>
      </Box>

      {/* ── RIGHT — Floating white card on the photo ── */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%' },
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3 },
        }}
      >
        {/* White card */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 370,
            bgcolor: '#ffffff',
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
            p: { xs: 3, sm: '28px 30px' },
          }}
        >
          {/* Card logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.6 }}>
            <img
              src={branding.logo || '/logo.png'}
              alt={branding.companyName}
              onError={(e) => { const t = e.currentTarget; t.onerror = null; t.style.display = 'none'; }}
              style={{
                maxWidth: 150, height: 82,
                objectFit: 'contain',
                borderRadius: '10px',
                imageRendering: '-webkit-optimize-contrast',
                flexShrink: 0,
              }}
            />
            {branding.companyName && (
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#111827', letterSpacing: '-0.2px' }}>
                {branding.companyName}
              </Typography>
            )}
          </Box>

          {/* ── Staff / Customer tabs — only on helpdesk.* domains ── */}
          {isHelpdeskMode() && !twoFactorRequired && (
            <Box sx={{ display: 'flex', bgcolor: '#f3f4f6', borderRadius: '8px', p: '3px', mb: 2, mt: 1 }}>
              {(['staff', 'customer'] as const).map((mode) => (
                <Box
                  key={mode}
                  onClick={() => { setLoginMode(mode); portalClearError(); clearError(); }}
                  sx={{
                    flex: 1, textAlign: 'center', py: '7px', borderRadius: '6px',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.18s',
                    userSelect: 'none',
                    ...(loginMode === mode
                      ? { bgcolor: '#fff', color: '#1f2937', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }
                      : { color: '#6b7280', '&:hover': { color: '#374151' } }),
                  }}
                >
                  {mode === 'staff' ? 'Agent Login' : 'Customer Login'}
                </Box>
              ))}
            </Box>
          )}

          {!twoFactorRequired && loginMode === 'staff' && (
            <>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a', mb: 0.5, letterSpacing: '-0.3px' }}>
                Login to Your Account
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 2.5 }}>
                Welcome back! Please login to continue.
              </Typography>
            </>
          )}
          {!twoFactorRequired && loginMode === 'customer' && (
            <>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a', mb: 0.5, letterSpacing: '-0.3px' }}>
                Customer Login
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 2.5 }}>
                Sign in to manage your support tickets.
              </Typography>
            </>
          )}

          {/* Error — staff */}
          {loginMode === 'staff' && (error || localError) && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
              {error || localError}
            </Alert>
          )}

          {/* Error — customer portal */}
          {loginMode === 'customer' && portalError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={portalClearError}>
              {portalError}
            </Alert>
          )}

          {/* ── CUSTOMER PORTAL FORM ── */}
          {loginMode === 'customer' && (
            <Box component="form" onSubmit={handlePortalSubmit}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.5 }}>Email Address</Typography>
              <TextField
                fullWidth type="email" value={portalEmail}
                onChange={(e) => setPortalEmail(e.target.value)}
                placeholder="you@company.com" variant="outlined" autoFocus size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af', fontSize: 17 }} /></InputAdornment> }}
                sx={{ mb: 1.5, ...fieldSx }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.5 }}>Password</Typography>
              <TextField
                fullWidth type={portalShowPw ? 'text' : 'password'} value={portalPassword}
                onChange={(e) => setPortalPassword(e.target.value)}
                placeholder="Enter your password" variant="outlined" size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#9ca3af', fontSize: 17 }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setPortalShowPw(v => !v)} edge="end" size="small" sx={{ color: '#9ca3af' }}>
                        {portalShowPw ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5, ...fieldSx }}
              />
              <Box sx={{ textAlign: 'right', mb: 2 }}>
                <Typography
                  component="span" onClick={() => navigate('/portal/forgot-password')}
                  sx={{ fontSize: 12, color: '#2563eb', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  Forgot Password?
                </Typography>
              </Box>
              <Button
                type="submit" variant="contained" fullWidth
                disabled={portalLoading || !portalEmail || !portalPassword}
                sx={{
                  py: 1.3, borderRadius: '6px', fontWeight: 600, fontSize: 15,
                  textTransform: 'none', bgcolor: '#2563eb',
                  boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                  '&:hover': { bgcolor: '#1e40af' },
                  '&.Mui-disabled': { bgcolor: '#93c5fd', color: '#fff' },
                  mb: 2,
                }}
              >
                {portalLoading ? <CircularProgress size={21} color="inherit" /> : 'Sign In to Portal'}
              </Button>
              <Typography sx={{ textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
                Don't have an account?{' '}
                <Box component="span"
                  onClick={() => navigate('/support')}
                  sx={{ color: '#2563eb', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  Submit a ticket without logging in
                </Box>
              </Typography>
            </Box>
          )}

          {/* ── STAFF LOGIN FORM ── */}
          {loginMode === 'staff' && (
            !twoFactorRequired ? (
            <Box component="form" onSubmit={handleSubmit}>

              {/* Email */}
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.5 }}>
                Email Address
              </Typography>
              <TextField
                fullWidth
                type={brandedDomain ? 'text' : 'email'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={brandedDomain ? `username@${brandedDomain}` : 'you@yourdomain.com'}
                variant="outlined"
                autoFocus
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: '#9ca3af', fontSize: 17 }} />
                    </InputAdornment>
                  ),
                  endAdornment: brandedDomain ? (
                    <InputAdornment position="end">
                      <Typography sx={{ color: '#9ca3af', fontSize: 12, whiteSpace: 'nowrap', userSelect: 'none' }}>
                        @{brandedDomain}
                      </Typography>
                    </InputAdornment>
                  ) : undefined,
                }}
                sx={{ mb: emailDomain && !brandedDomain ? 0.5 : 1.5, ...fieldSx }}
              />

              {/* Domain hint */}
              {emailDomain && !brandedDomain && (
                <Typography sx={{ fontSize: 11.5, color: '#6b7280', mb: 1.5 }}>
                  Domain detected:{' '}
                  <Box component="span" sx={{ fontWeight: 500, color: '#2563eb' }}>{emailDomain}</Box>
                </Typography>
              )}

              {/* Password */}
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.5 }}>
                Password
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#9ca3af', fontSize: 17 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: '#9ca3af' }}
                      >
                        {showPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5, ...fieldSx }}
              />

              {/* Remember / Forgot */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{ color: '#d1d5db', '&.Mui-checked': { color: '#2563eb' }, p: 0.5 }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 12, color: '#374151' }}>Remember Me</Typography>}
                  sx={{ m: 0 }}
                />
                <Typography
                  component={RouterLink}
                  to="/forgot-password"
                  sx={{ fontSize: 12, color: '#2563eb', fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Forgot Password?
                </Typography>
              </Box>

              {/* Sign In */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
                sx={{
                  py: 1.3, borderRadius: '6px',
                  fontWeight: 600, fontSize: 15,
                  textTransform: 'none',
                  bgcolor: '#2563eb',
                  boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                  '&:hover': { bgcolor: '#1e40af' },
                  '&.Mui-disabled': { bgcolor: '#93c5fd', color: '#fff' },
                  mb: 2,
                }}
              >
                {isLoading ? <CircularProgress size={21} color="inherit" /> : 'Sign In'}
              </Button>

              {/* New user */}
              <Typography sx={{ textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
                New user?{' '}
                <Box component="span" sx={{ color: '#2563eb', fontWeight: 500 }}>
                  Contact your administrator
                </Box>
              </Typography>
            </Box>

          ) : (

            /* ── OTP / 2FA FORM ── */
            <Box component="form" onSubmit={handleVerifyOtp}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                Verify Your Identity
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 2.5 }}>
                Enter the 6-digit code sent to you
              </Typography>

              <Alert
                severity="info"
                icon={twoFactorRequired.otpMethod === 'totp' ? undefined : (twoFactorRequired.otpMethod === 'email' ? undefined : <SmsIcon />)}
                sx={{
                  mb: 2.5, borderRadius: 1.5,
                  bgcolor: '#eff6ff', color: '#1d4ed8',
                  '& .MuiAlert-icon': { color: '#3b82f6' },
                }}
              >
                {twoFactorRequired.otpMethod === 'totp' ? (
                  <>Enter the 6-digit code from your authenticator app (e.g. 1Auth, Google Authenticator)</>
                ) : twoFactorRequired.otpMethod === 'email' ? (
                  <>A verification code has been sent to your email{twoFactorRequired.emailHint && ` (${twoFactorRequired.emailHint})`}</>
                ) : (
                  <>A verification code has been sent to your phone{twoFactorRequired.phoneHint && ` ending in ${twoFactorRequired.phoneHint}`}</>
                )}
              </Alert>

              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.5 }}>
                Verification Code
              </Typography>
              <TextField
                fullWidth
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                variant="outlined"
                size="small"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SmsIcon sx={{ color: '#9ca3af', fontSize: 17 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1, ...fieldSx,
                  '& .MuiInputBase-input': { fontSize: '1.2rem', letterSpacing: '0.4em', textAlign: 'center' },
                }}
              />

              {twoFactorRequired.otpMethod !== 'totp' && (
                <Box sx={{ textAlign: 'right', mb: 2.5 }}>
                  <Typography
                    component="span"
                    onClick={handleResendOtp}
                    sx={{ color: '#2563eb', fontWeight: 500, fontSize: 12, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Resend Code
                  </Typography>
                </Box>
              )}
              {twoFactorRequired.otpMethod === 'totp' && <Box sx={{ mb: 2.5 }} />}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading || otp.length !== 6}
                sx={{
                  py: 1.3, borderRadius: '6px',
                  fontWeight: 600, fontSize: 15, textTransform: 'none',
                  bgcolor: '#dc2626',
                  boxShadow: '0 4px 14px rgba(220,38,38,0.35)',
                  '&:hover': { bgcolor: '#b91c1c' },
                  '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#fff' },
                  mb: 1.5,
                }}
              >
                {isLoading ? <CircularProgress size={21} color="inherit" /> : 'Verify & Sign In'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={handleBackToLogin}
                sx={{
                  py: 0.8, fontSize: 13, fontWeight: 500,
                  textTransform: 'none', color: '#6b7280',
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                ← Back to Login
              </Button>
            </Box>
          )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
